import React from 'react';
import { Button, Field, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import { getPageContent, useAppActions, useAppState } from '../../../context';
import AppSignatureField from '../../../standard-components/signatureField';
import { AppInspectionRecordInput } from './recordInput';
import {
  ActivityGroupView,
  isPageReferenceActivity,
  isPageReferenceActivityGroup,
} from '@gkn-ili/views/dist/OrderDocumentation';
import { getParentPageReference } from '../../../utils/orderDocumentationHelpers';
import { InspectionRecordItem } from '@gkn-ili/views/dist/InspectionRecord';

const backgroundColor = tokens.colorBrandBackground2;

const useStyles = makeStyles({
  recordContainer: {
    flex: '0 0 auto',
    minWidth: '300px',
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.borderLeft('1px', 'solid', 'gray'),
    backgroundColor: backgroundColor,
    '& >*': {
      ...shorthands.margin('15px'),
    },
  },
  title: {
    fontSize: tokens.fontSizeHero800,
    lineHeight: tokens.lineHeightHero800,
  },
  horizontalButtonContainer: {
    flex: '0 0 auto',
    display: 'flex',
    flexDirection: 'row',
    gap: tokens.spacingHorizontalXS,
  },
  flexFiller: {
    flex: '1 0 0px',
  },
  nextButton: {
    ...shorthands.gridArea(4, 2, 5, 3),
  },
  inputContainer: {},
  fieldInput: {
    maxWidth: '160px',
  },
  fieldContainer: {
    boxSizing: 'content-box',
    ...shorthands.padding(tokens.spacingVerticalXXS),
    ...shorthands.border('1px', 'solid', backgroundColor),
  },
  fieldContainerHighlighted: {
    ...shorthands.border('1px', 'solid', 'lightgray'),
  },
});

export default function AppInspectionRecordContainer(): JSX.Element {
  const styles = useStyles();
  const appState = useAppState();
  const appActions = useAppActions();
  // Get the activity group page for the signature fields
  const activityGroupReference = isPageReferenceActivity(appState.activePage)
    ? getParentPageReference(appState.activePage)
    : isPageReferenceActivityGroup(appState.activePage)
      ? appState.activePage
      : undefined;
  const activityGroupPage = activityGroupReference
    ? (getPageContent(appState.orderView, activityGroupReference) as ActivityGroupView)
    : undefined;
  const performed = activityGroupPage?.performed;
  const checked = activityGroupPage?.checked;
  // Get the activity page for the data fields
  const activityReference = isPageReferenceActivity(appState.activePage) ? appState.activePage : undefined;
  const activityPages = activityGroupPage ? activityGroupPage.activities : undefined;

  const [recordState, setRecordState] = React.useState<{
    records: Record<string, string | number | undefined | null>;
    isBusy: boolean;
  }>({
    records: {},
    isBusy: false,
  });
  const isPartiallySigned = !!performed || !!checked;

  function formatRecordToString(value: undefined | string | number | null): string | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }
    if (typeof value === 'number') {
      return value.toString();
    }
    return value;
  }

  function recordComponent(key: string, record: InspectionRecordItem): JSX.Element {
    return (
      <Field
        key={key}
        label={record.type}
        className={mergeClasses(
          styles.fieldContainer,
          isPageReferenceActivity(appState.activePage) &&
            key === appState.activePage.activityKey &&
            styles.fieldContainerHighlighted,
        )}
      >
        <AppInspectionRecordInput
          className={styles.fieldInput}
          item={record}
          disabled={recordState.isBusy || isPartiallySigned}
          value={formatRecordToString(
            activityReference?.activityKey ? recordState.records[activityReference?.activityKey] : '',
          )}
          onChange={(data) =>
            setRecordState((current) => {
              if (current.isBusy || isPartiallySigned) {
                return current;
              }
              return {
                ...current,
                records: { ...current.records, [activityReference?.activityKey ?? '_none_']: data },
              };
            })
          }
        />
      </Field>
    );
  }

  const inspectionFields = activityPages
    ? Object.entries(activityPages).flatMap(([key1, activityPage]) =>
        Object.entries(activityPage.records).map(([key2, record]) => recordComponent(key1 + '_' + key2, record)),
      )
    : undefined;

  const gotoNext = () => {
    appActions.gotoNextInspectionStep();
  };
  return (
    <div className={styles.recordContainer}>
      <div className={styles.title}>Inspection records</div>
      {inspectionFields}
      <div className={styles.flexFiller} />
      {performed !== undefined && checked !== undefined && activityGroupReference && (
        <div className={styles.horizontalButtonContainer}>
          <Field className={styles.inputContainer} label="Performed">
            <AppSignatureField
              loading={recordState.isBusy && !isPartiallySigned}
              signature={performed}
              onSign={async (userId: string, employeeCode: string, date: Date) => {
                setRecordState((current) => ({ ...current, isBusy: true }));
                await appActions.executeOrder.signInspectionRecords(
                  { ...activityGroupReference, item: 'performed' },
                  { userId, employeeCode, date },
                );
                setRecordState((current) => ({ ...current, isBusy: false }));
              }}
            />
          </Field>
          <Field className={styles.inputContainer} label="Checked">
            <AppSignatureField
              loading={recordState.isBusy && isPartiallySigned}
              disabled={!performed}
              signature={checked}
              onSign={async (userId: string, employeeCode: string, date: Date) => {
                setRecordState((current) => ({ ...current, isBusy: true }));
                await appActions.executeOrder.signInspectionRecords(
                  { ...activityGroupReference, item: 'checked' },
                  { userId, employeeCode, date },
                );
                setRecordState((current) => ({ ...current, isBusy: false }));
              }}
            />
          </Field>
        </div>
      )}
      <div className={styles.horizontalButtonContainer}>
        <Button
          className={styles.nextButton}
          appearance={activityGroupReference && checked ? 'secondary' : 'primary'}
          size="large"
          onClick={gotoNext}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
